import React from 'react';
import { motion } from 'framer-motion';
import { Database, Shield, Tag, ShoppingCart } from 'lucide-react';
import { GroupRenderProps } from './types';

// Map of topic IDs to their icons and descriptions
const TOPIC_DETAILS = {
  'CDP Enhancement': {
    icon: Database,
    description: 'Improve customer data platform capabilities'
  },
  'Data Governance & Compliance': {
    icon: Shield,
    description: 'Ensure compliance with data regulations'
  },
  'Tag Management': {
    icon: Tag,
    description: 'Streamline tag implementation and management'
  },
  'Retail Media Networks': {
    icon: ShoppingCart,
    description: 'Optimize retail media network solutions'
  }
};

export const GroupRenderer: React.FC<GroupRenderProps> = ({
  groupLabel,
  items,
  selectedSections,
  searchQuery,
  onGroupToggle,
  onSectionToggle,
  isTopicGroup
}) => {
  // Ensure selectedSections is always an array
  const sections = Array.isArray(selectedSections) ? selectedSections : [];
  
  // Check if group should show up based on search
  if (!items || !groupMatchesSearch(items, searchQuery, isTopicGroup)) return null;

  // Check group checkbox state
  const itemIds = items.map((item) => (isTopicGroup ? item.name : `${groupLabel}:${item.id}`));
  const allSelected = itemIds.every((id) => sections.includes(id)) && sections.includes(groupLabel);
  const someSelected = !allSelected && (sections.includes(groupLabel) || itemIds.some((id) => sections.includes(id)));

  return (
    <div className="border-b border-gray-200 dark:border-gray-600">
      {/* Group header */}
      <div
        className="flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-gray-800 cursor-pointer"
        onClick={() => onGroupToggle(groupLabel, items, isTopicGroup)}
      >
        <div className="flex items-center">
          <div className="relative flex items-center justify-center mr-3">
            <input type="checkbox" checked={allSelected} onChange={() => {}} className="sr-only" />
            <motion.div
              initial={false}
              animate={{
                backgroundColor: allSelected
                  ? 'rgba(59, 130, 246, 1)'
                  : someSelected
                  ? 'rgba(59, 130, 246, 0.4)'
                  : 'rgba(59, 130, 246, 0)',
                borderColor: allSelected || someSelected
                  ? 'rgb(59, 130, 246)'
                  : 'rgb(209, 213, 219)'
              }}
              className="h-4 w-4 rounded border-2 flex items-center justify-center"
            >
              {allSelected && (
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="text-white">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 11.5L11 15L16 9"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </motion.div>
              )}
              {someSelected && !allSelected && (
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="text-white">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 12H16"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </motion.div>
              )}
            </motion.div>
          </div>
          <span className={`font-medium text-sm ${
            allSelected || someSelected
              ? 'text-blue-700 dark:text-blue-300'
              : 'text-gray-700 dark:text-gray-300'
          }`}>
            {groupLabel}
          </span>
        </div>
      </div>

      {/* Items */}
      {items
        .filter((item) =>
          item.name.toLowerCase().includes(searchQuery.toLowerCase() || '')
        )
        .map((item) => {
          const sectionId = isTopicGroup ? item.name : `${groupLabel}:${item.id}`;
          const checked = sections.includes(sectionId);
          
          // Get topic details if this is a topic group
          const topicDetails = isTopicGroup ? TOPIC_DETAILS[item.name as keyof typeof TOPIC_DETAILS] : null;
          const IconComponent = topicDetails?.icon;
          
          return (
            <motion.div
              key={item.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
              onClick={() => onSectionToggle(sectionId)}
              className={`flex items-start px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer pl-8 ${isTopicGroup ? 'pb-3' : ''}`}
            >
              <div className="flex flex-col w-full">
                <div className="flex items-center">
                  <motion.div
                    className="relative flex items-center justify-center mr-3"
                    whileTap={{ scale: 0.9 }}
                  >
                    <input type="checkbox" checked={checked} onChange={() => {}} className="sr-only" />
                    <motion.div
                      initial={false}
                      animate={{
                        backgroundColor: checked
                          ? 'rgba(59, 130, 246, 1)'
                          : 'rgba(59, 130, 246, 0)',
                        borderColor: checked ? 'rgb(59, 130, 246)' : 'rgb(209, 213, 219)'
                      }}
                      className="h-4 w-4 rounded border-2 flex items-center justify-center"
                    >
                      {checked && (
                        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="text-white">
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M8 11.5L11 15L16 9"
                              stroke="white"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </motion.div>
                      )}
                    </motion.div>
                  </motion.div>
                  
                  {/* Display icon for topics */}
                  {isTopicGroup && IconComponent && (
                    <IconComponent className={`h-5 w-5 mr-2 ${
                      checked
                        ? 'text-blue-600 dark:text-blue-400'
                        : 'text-gray-500 dark:text-gray-400'
                    }`} />
                  )}
                  
                  <span className={`text-sm ${
                    isTopicGroup && checked
                      ? 'font-medium text-blue-700 dark:text-blue-300'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}>
                    {item.name}
                  </span>
                </div>
                
                {/* Display description for topics */}
                {isTopicGroup && topicDetails?.description && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-10">
                    {topicDetails.description}
                  </p>
                )}
              </div>
            </motion.div>
          );
        })}
    </div>
  );
};

// Helper to check if a group should show up based on the search
const groupMatchesSearch = (
  items: { id: string; name: string }[],
  searchQuery: string,
  isTopicGroup?: boolean
): boolean => {
  if (!searchQuery.trim()) return true;
  return items.some((item) =>
    (isTopicGroup ? item.name : item.name).toLowerCase().includes(searchQuery.toLowerCase())
  );
};
